// Route: /notifications — полная страница уведомлений.
// На mobile — основной вход в центр уведомлений (вместо панели/sheet).
// На desktop — полноразмерный вид; sidepanel-овая вариация открывается из Topbar.
// Spec: docs/ui-spec/notifications.panel.desktop.md + notifications.sheet.mobile.md
//
// Тактильный редизайн «Картотека» (§8.V): центр уведомлений = панель e2;
// строки внутри — тактильные карточки e1, сгруппированные по датам.
//
// Источник данных — PowerSync stream `user_notifications` (sync-rules.yaml).
// Чтение через usePowerSyncQuery → реактивный SQL поверх локальной SQLite.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { ElevatedPanel } from "@studyhub/ui/components/studyhub/elevated-panel";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { IconBell, IconSettings } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { adaptNotification } from "@/components/notifications/notification-adapter";
import type { NotificationItemData } from "@/components/notifications/notification-item";
import { NotificationList } from "@/components/notifications/notification-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useSession } from "@/hooks/use-session";
import { useNotificationsListQuery } from "@/lib/powersync/queries/use-notifications-query";

const searchSchema = z.object({
	demo: z.enum(["loading", "error", "empty"]).optional(),
});

export const Route = createFileRoute("/notifications/")({
	validateSearch: tolerantSearch(searchSchema),
	component: NotificationsRoute,
	pendingComponent: PagePendingFallback,
});

function NotificationsRoute() {
	const { t } = useTranslation("notifications");
	const search = Route.useSearch();
	const { user } = useSession();
	const { data: rows, isLoading } = useNotificationsListQuery(user?.id);

	const items: NotificationItemData[] = useMemo(() => (rows ?? []).map(adaptNotification), [rows]);

	const unreadCount = useMemo(() => items.filter((i) => !i.read).length, [items]);

	return (
		<div className="mx-auto w-full max-w-2xl px-4 py-6 lg:py-10">
			<SectionHeader
				className="mb-4"
				icon={<IconBell stroke={1.75} />}
				category="time"
				title={t("title")}
				description={t("unreadCount", { count: unreadCount })}
				actions={
					<Button
						asChild
						variant="outline"
						size="sm"
						className="min-h-[44px] cursor-pointer lg:min-h-0"
					>
						<Link to="/settings/notifications">
							<IconSettings size={14} />
							<span className="hidden sm:inline">{t("settings")}</span>
						</Link>
					</Button>
				}
			/>

			<ElevatedPanel className="overflow-hidden">
				<div className="h-[70dvh] min-h-[400px]">
					<NotificationList items={items} isLoading={isLoading} demo={search.demo} />
				</div>
			</ElevatedPanel>
		</div>
	);
}
